import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { SuperAdminService } from '@core/services/super-admin.service';
import { autoUnsubWith, Utility } from '@shared/services/utility.abstract';
import { DialogControlService } from '@shared/services/dialog-control.service';
import { PaymentSucessComponent } from '../booking-flow/payment-success/payment-success.component';
import { USER_ROLES, UPDATED_USER_TYPE } from '@shared/constant/shared.constant';
import { EncryptionStorageService } from '@core/services/encryption-storage.service';
import { BookingFlowService } from '../booking-flow/service/booking-flow.service';
import { FilterService } from '@shared/services/filter.service';
import { DateTime } from 'luxon';

@Component({
  selector: 'app-update-payment-date-modal',
  templateUrl: './update-payment-date-modal.component.html',
  styleUrls: ['./update-payment-date-modal.component.scss'],
})
export class UpdatePaymentDateModalComponent implements OnInit {
  readonly destroyRef = inject(DestroyRef);
  paymentDateForm: FormGroup;
  bookingDetails: any;
  minDate: Date;
  maxDate: Date;
  errorMessage: string = '';
  isLoading: boolean = false;
  userRoles = USER_ROLES;
  userType = UPDATED_USER_TYPE;
  assistedListUrl: string;

  constructor(
    private readonly fb: FormBuilder,
    private readonly ref: DynamicDialogRef,
    private readonly config: DynamicDialogConfig,
    private readonly superAdminService: SuperAdminService,
    private readonly dialogControlService: DialogControlService,
    private readonly encryptStorage: EncryptionStorageService,
    private readonly bookingFlowService: BookingFlowService,
    private readonly filterService: FilterService
  ) {}

  ngOnInit(): void {
    this.bookingDetails = this.config?.data?.bookingData;
    this.calculateDateRange();
    this.paymentDateForm = this.fb.group({
      paymentDate: [new Date(), Validators.required],
    });
  }

  calculateDateRange(): void {
    this.minDate = new Date(this.bookingDetails.startDate);
    this.maxDate = DateTime.fromJSDate(new Date(this.bookingDetails.endDate)).minus({ days: 1 }).toJSDate();
  }

  onSubmit(): void {
    if (this.paymentDateForm.invalid) {
      this.paymentDateForm.markAllAsTouched();
      return;
    }

    this.isLoading = true;
    this.errorMessage = '';
    this.filterService.preserveDataOnNextNavigation = true;

    const selectedDate: Date = this.paymentDateForm.value.paymentDate;
    const day = String(selectedDate.getDate()).padStart(2, '0');
    const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
    const year = selectedDate.getFullYear();

    const request = {
      bookingId: this.bookingDetails.id,
      arrivalDateTime: null,
      departureDateTime: null,
      slotId: this.bookingDetails?.slotId || null,
      timezone: Utility.getLocalTimeZone(),
      billingDate: `${day}-${month}-${year}`,
    };

    this.bookingFlowService
      .updateBookingById(request)
      .pipe(autoUnsubWith(this.destroyRef))
      .subscribe({
        next: (_response: any) => {
          this.isLoading = false;
          this.superAdminService.triggerPopupClose(true);
          this.setAssistedListUrl();
          this.ref.close(true);
          this.dialogControlService.openDialog(PaymentSucessComponent, {
            width: '30%',
            data: {
              assistedUrl: this.assistedListUrl,
              bodyText: 'Payment Date Updated Successfully',
              buttonText: 'OK',
              heading: 'Success',
              bookingId: this.bookingDetails.id,
            },
          });
        },
        error: (error) => {
          this.isLoading = false;
          this.errorMessage =
            error?.error?.message || 'Failed to update payment date.';
        },
      });
  }

  setAssistedListUrl(): void {
    const roleName = this.encryptStorage.getItem('roleName');
    const bookingDetailsPath =
      this.bookingDetails.userType === this.userType.PARKER
        ? 'booking-details'
        : 'group-booking-details';

    switch (roleName) {
      case this.userRoles.SUPER_ADMIN:
        this.assistedListUrl = `super-admin/${bookingDetailsPath}`;
        break;
      case this.userRoles.PARKING_MANAGER:
      case this.userRoles.PARKING_OWNER:
        this.assistedListUrl = `manager/${bookingDetailsPath}`;
        break;
    }
  }

  closeDialog(): void {
    this.ref.close();
  }
}
